import React, { useEffect, useState, useRef } from 'react';
import { navigate } from 'gatsby';
import { CloseOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import * as styles from './index.module.scss';
import type { FloatingNewsProps, NewsItem } from './types';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { newsMgr } from '@/grpc/domain/hall/news';
import { newItemToV1Item } from '@/store/newsStore';
import { NewsCategory } from '@/grpc/client/bpt/hall/news/v1/news';
import { newsServiceMgrNoAuth } from '@/grpc/news.service';

const PAGE_SIZE = 1;

type Listener = (isVisible: boolean, height: number, shouldAnimate: boolean) => void;
const listeners: Set<Listener> = new Set();

export const subscribeFloatingNews = (listener: Listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const notifyListeners = (isVisible: boolean, height: number = 0, shouldAnimate: boolean = false) => {
  listeners.forEach(listener => listener(isVisible, height, shouldAnimate));
};

// 全局变量，用于跨路由保持状态
let hasShownAnimation = false;

// 检查新闻是否已经被查看过
const hasNewsBeenViewed = (newsId: string): boolean => {
  if (typeof window === 'undefined') return false;
  const viewedNews = localStorage.getItem('mtt_viewed_news');
  if (!viewedNews) return false;
  try {
    const viewedNewsIds = JSON.parse(viewedNews);
    return viewedNewsIds.includes(newsId);
  } catch (e) {
    return false;
  }
};

// 记录新闻已查看
const markNewsAsViewed = (newsId: string) => {
  if (typeof window === 'undefined') return;
  try {
    const viewedNews = localStorage.getItem('mtt_viewed_news');
    const viewedNewsIds = viewedNews ? JSON.parse(viewedNews) : [];
    if (!viewedNewsIds.includes(newsId)) {
      viewedNewsIds.push(newsId);
      localStorage.setItem('mtt_viewed_news', JSON.stringify(viewedNewsIds));
    }
  } catch (e) {
    console.error('Error saving news history:', e);
  }
};

const FloatingNews: React.FC<FloatingNewsProps> = ({
  isVisible = true,
  onClose,
  onNewsClick,
  className,
}) => {
  if (!isVisible) {
    return null;
  }

  const [isNewsViewed, setIsNewsViewed] = useState(true);
  const [isShouldShow, setIsShouldShow] = useState(false);
  const [shouldExit, setShouldExit] = useState(false);
  const floatingNewsRef = useRef<HTMLDivElement>(null);
  
  // 获取最新新闻数据
  const { data: latestNews, isSuccess } = useQuery({
    queryKey: ['floating-news'],
    queryFn: async () => {
      // 移除数据，不展示 0415
      return null

      const result = await newsServiceMgrNoAuth.effectiveListNews(
        {
          channelID: "network",
          page: 1,
          pageSize: PAGE_SIZE,
          category: NewsCategory.NewsCategoryAll,
          sortByTopAt: true,
        },
        {
          _t: Date.now(),
          meta: {
            'Client-Language': 'en',
          }
        }
      );
      
      if (result.newsList.length === 0) {
        return null;
      }
      
      const newsItem = newItemToV1Item(result.newsList[0]);
      return {
        id: newsItem.id,
        title: newsItem.newsTitle,
        summary: newsItem.abstract,
        imageUrl: newsItem.mainImage,
        publishedAt: newsItem.publicTimeDesc,
        pcBannerImage: newsItem.pcBannerImage,
        mobileBannerImage: newsItem.mobileBannerImage,
        jumpLink: newsItem.jumpLink,
      };
    },
    staleTime: 1000 * 60 * 5,
  });

  // 在客户端检查新闻历史
  useEffect(() => {
    if (typeof window !== 'undefined' && latestNews) {
      const hasViewed = hasNewsBeenViewed(latestNews.id);
      setIsNewsViewed(hasViewed);
    }
  }, [latestNews]);

  // 确保组件完全准备好后再显示
  useEffect(() => {
    setIsShouldShow((!latestNews) == false && !isNewsViewed);
  }, [latestNews, isNewsViewed]);

  // 监听高度变化
  useEffect(() => {
    if (!floatingNewsRef.current) return;
    const resizeObserver = new ResizeObserver(entries => {
      const height = entries[0]?.contentRect.height || 0;
      notifyListeners(isShouldShow, height, !hasShownAnimation);
    });
    
    resizeObserver.observe(floatingNewsRef.current);
    return () => {
      resizeObserver.disconnect();
      notifyListeners(false, 0, false);
    };
  }, [isShouldShow]);

  if (!isShouldShow || !latestNews) return null;

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    markNewsAsViewed(latestNews.id);
    // 先触发退出动画
    setShouldExit(true);
    // 通知外部开始退出动画
    notifyListeners(false, floatingNewsRef.current?.getBoundingClientRect().height || 0, true);
  };

  const handleClick = (e: React.MouseEvent) => {
    if (latestNews.jumpLink) {
      window.open(latestNews.jumpLink, '_blank');
    } else {
      onNewsClick?.(latestNews.id);
    }
    handleClose(e);
  };

  // 确定是否需要动画
  const shouldAnimate = !hasShownAnimation;

  return (
    <AnimatePresence mode="wait" presenceAffectsLayout>
      {!shouldExit && (
        <motion.div
          ref={floatingNewsRef}
          className={classNames(styles.floatingNews, className)}
          initial={shouldAnimate ? { opacity: 0, y: -40, x: "-50%" } : false}
          animate={{ opacity: 1, y: 0, x: "-50%" }}
          exit={{ opacity: 0, y: -40, x: "-50%" }}
          transition={{ 
            duration: 0.3,
            layout: { duration: 0 }
          }}
          layoutId="floating-news"
          onClick={handleClick}
          onAnimationComplete={(definition) => {
            if (definition === "exit") {
              // 退出动画完成后更新状态
              setIsNewsViewed(true);
              setShouldExit(false);
              notifyListeners(false, 0, false);
              onClose?.();
            }
            if (!hasShownAnimation) {
              hasShownAnimation = true;
            }
          }}
        >
          {/* PC端背景图 */}
          <img 
            src={latestNews.pcBannerImage} 
            alt=""
            className={classNames(styles.backgroundImage, styles.pc)}
          />
          {/* 移动端背景图 */}
          <img 
            src={latestNews.mobileBannerImage} 
            alt=""
            className={classNames(styles.backgroundImage, styles.mobile)}
          />
          <div className={styles.closeButtonContainer} onClick={handleClose}>
            <CloseIcon />
          </div>
          <div className={styles.textContent}>
            <div className={styles.title}>{latestNews.title}</div>
            <div className={styles.summary}>{latestNews.summary}</div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M11.5984 4.3999L4.39844 11.5999M4.39844 4.3999L11.5984 11.5999" stroke="#C6E8E9" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export default FloatingNews; 